import { Component, signal, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  colombianPhoneValidator,
  emailFormatValidator
} from '@view/shared/validators/form-validators';
import { InputTextComponent } from '@view/shared/molecules/input-text/input-text.component';
import { FormDropdownComponent } from '@view/shared/molecules/form-dropdown/form-dropdown.component';
import { FormDateComponent } from '@view/shared/molecules/form-date/form-date.component';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PatientSearchResult } from '@model/patient/patient.model';
import {
  ManualEntryPayload,
  ManualEntryResponse,
  SaveSampleByCodePayload,
  EntryByCodeResponse
} from '@model/patient/sample-entry.model';
import {
  SampleFormValue,
  AddSampleBaseComponent
} from '../shared/base/add-sample-base.component';
import { AddSampleManualComponent } from '../components/manual/add-sample-manual.component';
import {
  KinshipType,
  FamilyMemberData
} from '@infra-services/exam/exam-type.interface';
import { FamilyMembersModalComponent, AffecteeOrderData } from '../components/family-members-modal/family-members-modal.component';
import { CreateFamilyMembersResponse, FamilyExamType } from '@model/family-member/create-family-members.model';
import { ExamCatalogComponent } from '@view/administrative/exam-catalog/exam-catalog.component';

type IngestionMethod = 'code' | 'id' | 'manual' | 'exam-catalog';

@Component({
  selector: 'app-add-sample',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextComponent,
    FormDropdownComponent,
    FormDateComponent,
    ButtonComponent,
    AddSampleManualComponent,
    FamilyMembersModalComponent,
    ExamCatalogComponent
  ],
  templateUrl: './add-sample.component.html',
  styleUrls: ['../shared/styles/add-sample.component.scss']
})
export class AddSampleComponent
  extends AddSampleBaseComponent
  implements OnInit, OnDestroy
{
  private readonly MSG_ERROR_GUARDAR = 'Error al guardar';

  selectedMethod = signal<IngestionMethod | null>(null);
  hoveredMethod = signal<IngestionMethod | null>(null);
  showCodeSearch = signal(false);

  @ViewChild('manualEntryModal') manualEntryModal!: AddSampleManualComponent;

  @ViewChild('familyMembersModal')
  familyMembersModal!: FamilyMembersModalComponent;

  showFamilyMembersModal = signal(false);
  savedLgNumber = signal('');
  savedOrderId = signal('');
  savedExamType = signal<FamilyExamType | null>(null);
  savedExamTypeId = signal('');
  savedPlacerOrderNumber = signal('');
  KinshipType = KinshipType;

  get affecteeOrderData(): AffecteeOrderData {
    return {
      siteId: this.sampleForm.get('site')?.value ?? '',
      priorityId: this.sampleForm.get('priorityId')?.value ?? '',
      laboratoryId: this.sampleForm.get('laboratoriesId')?.value ?? '',
      diagnosisId: this.sampleForm.get('diagnosesId')?.value ?? '',
      specialistId: this.sampleForm.get('specialistsId')?.value ?? '',
      providerId: this.sampleForm.get('providerId')?.value ?? '',
      companyId: this.sampleForm.get('companyId')?.value ?? '',
      regionalId: this.sampleForm.get('region')?.value ?? ''
    };
  }
  selectedKinshipType: KinshipType | null = null;

  get isSearchByIdDisabled(): boolean {
    const identifierType = this.searchForm.get('identifierType')?.value;
    const documentNumber = this.searchForm.get('documentNumber')?.value;
    return !identifierType || !documentNumber || this.isSearching();
  }

  get isSearchByCodeDisabled(): boolean {
    const specimenCode = this.searchForm.get('specimenCode')?.value;
    return !specimenCode || this.isSearching();
  }

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  selectIngestionMethod(method: IngestionMethod): void {
    const previousMethod = this.selectedMethod();
    if (previousMethod !== method) {
      this.showForm.set(false);
      this.searchValue.set('');
      this.caseNumber.set('');
      this.searchForm.reset();
      this.sampleForm.reset();
    }
    this.selectedMethod.set(method);

    const optionalFields = [
      'emailAddress',
      'specialistsId',
      'middleName',
      'maternalFamilyName',
      'userDataDescription',
      'descriptionDataSample',
      'descriptionDataExam',
      'clinicalHistory'
    ];

    optionalFields.forEach((field) => {
      const control = this.sampleForm.get(field);
      if (field === 'emailAddress') {
        control?.setValidators([emailFormatValidator()]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    const commonRequiredFields = [
      'identifierType',
      'givenName',
      'familyName',
      'administrativeSex',
      'birthDate',
      'region',
      'specimenType',
      'examType',
      'placerOrderNumber',
      'site',
      'laboratoriesId',
      'diagnosesId',
      'priorityId'
    ];

    commonRequiredFields.forEach((field) => {
      this.sampleForm.get(field)?.setValidators([Validators.required]);
      this.sampleForm.get(field)?.updateValueAndValidity();
    });

    if (method === 'manual') {
      this.showForm.set(true);
      this.sampleForm.get('idNumber')?.enable();

      this.sampleForm.get('examCode')?.clearValidators();
      this.sampleForm.get('examCode')?.updateValueAndValidity();
      this.sampleForm.get('admissionDate')?.clearValidators();
      this.sampleForm.get('admissionDate')?.updateValueAndValidity();

      this.sampleForm.get('idNumber')?.setValidators([Validators.required]);
      this.sampleForm.get('idNumber')?.updateValueAndValidity();
      this.sampleForm
        .get('maritalStatus')
        ?.setValidators([Validators.required]);
      this.sampleForm.get('maritalStatus')?.updateValueAndValidity();
      this.sampleForm
        .get('telephoneNumber')
        ?.setValidators([Validators.required, colombianPhoneValidator()]);
      this.sampleForm.get('telephoneNumber')?.updateValueAndValidity();
      this.sampleForm
        .get('specimenContainerId')
        ?.setValidators([Validators.required]);
      this.sampleForm.get('specimenContainerId')?.updateValueAndValidity();
      this.sampleForm.get('companyId')?.setValidators([Validators.required]);
      this.sampleForm.get('companyId')?.updateValueAndValidity();
      this.sampleForm.get('providerId')?.setValidators([Validators.required]);
      this.sampleForm.get('providerId')?.updateValueAndValidity();
    } else {
      this.sampleForm.get('idNumber')?.disable();

      this.sampleForm.get('examCode')?.setValidators([Validators.required]);
      this.sampleForm.get('examCode')?.updateValueAndValidity();
      this.sampleForm
        .get('admissionDate')
        ?.setValidators([Validators.required]);
      this.sampleForm.get('admissionDate')?.updateValueAndValidity();

      this.sampleForm.get('patientNumber')?.clearValidators();
      this.sampleForm.get('patientNumber')?.updateValueAndValidity();
      this.sampleForm.get('maritalStatus')?.clearValidators();
      this.sampleForm.get('maritalStatus')?.updateValueAndValidity();
      this.sampleForm.get('race')?.clearValidators();
      this.sampleForm.get('race')?.updateValueAndValidity();
      this.sampleForm.get('telephoneNumber')?.clearValidators();
      this.sampleForm.get('telephoneNumber')?.updateValueAndValidity();
      this.sampleForm.get('streetAddress')?.clearValidators();
      this.sampleForm.get('streetAddress')?.updateValueAndValidity();
      this.sampleForm.get('specimenContainerId')?.clearValidators();
      this.sampleForm.get('specimenContainerId')?.updateValueAndValidity();

      // Campos que DEBEN ser obligatorios también para ingreso por ID y Código
      this.sampleForm.get('companyId')?.setValidators([Validators.required]);
      this.sampleForm.get('companyId')?.updateValueAndValidity();
      this.sampleForm.get('providerId')?.setValidators([Validators.required]);
      this.sampleForm.get('providerId')?.updateValueAndValidity();
    }
  }

  activateCodeSearch(): void {
    this.showCodeSearch.set(true);
  }

  searchByCode(): void {
    const specimenCode = this.searchForm.get('specimenCode')?.value as string;

    if (
      !specimenCode ||
      typeof specimenCode !== 'string' ||
      specimenCode.trim() === ''
    ) {
      return;
    }

    this.searchValue.set(specimenCode);
    this.isSearching.set(true);

    this.presenter.searchPatientByCode(specimenCode).subscribe({
      next: (result: PatientSearchResult) => {
        this.isSearching.set(false);
        if (result.found) {
          this.showForm.set(true);
          this.populateForm(result);
        } else {
          this.toastService.setToast({
            type: ToastTypeEnum.WARNING,
            title: 'Muestra no encontrada',
            message: 'No se encontraron datos para el código ingresado'
          });
        }
      },
      error: () => {
        this.isSearching.set(false);
        this.toastService.setToast({
          type: ToastTypeEnum.ERROR,
          title: 'Error en la búsqueda',
          message:
            'Ocurrió un error al buscar la muestra. Por favor, intente nuevamente.'
        });
      }
    });
  }

  searchPatientById(): void {
    const identifierType = this.searchForm.get('identifierType')
      ?.value as string;
    const documentNumber = this.searchForm.get('documentNumber')
      ?.value as string;

    if (
      !documentNumber ||
      typeof documentNumber !== 'string' ||
      documentNumber.trim() === '' ||
      !identifierType
    ) {
      return;
    }
    this.caseNumber.set('');
    this.searchValue.set(documentNumber);
    this.isSearching.set(true);

    this.presenter.searchPatientById(identifierType, documentNumber).subscribe({
      next: (result: PatientSearchResult) => {
        this.isSearching.set(false);
        if (result.found) {
          this.showForm.set(true);
          this.populateForm(result);
        } else {
          this.caseNumber.set('');
          this.showForm.set(false);
          this.toastService.setToast({
            type: ToastTypeEnum.WARNING,
            title: result.message ? 'Muestra no disponible' : 'Paciente no encontrado',
            message: result.message || 'No se encontraron datos para el documento ingresado'
          });
        }
      },
      error: () => {
        this.isSearching.set(false);
        this.caseNumber.set('');
        this.showForm.set(false);
        this.toastService.setToast({
          type: ToastTypeEnum.ERROR,
          title: 'Error en la búsqueda',
          message:
            'Ocurrió un error al buscar el paciente. Por favor, intente nuevamente.'
        });
      }
    });
  }

  override clearSearch(): void {
    this.showForm.set(false);
    this.searchValue.set('');
    this.selectedMethod.set(null);
    this.showCodeSearch.set(false);
    this.caseNumber.set('');
    this.searchForm.reset();
    this.sampleForm.reset();
  }

  override populateForm(data: PatientSearchResult): void {
    this.caseNumber.set(data.caseNumber ?? '');

    this.sampleForm.patchValue({
      identifierType: data.identifierTypeCode,
      idNumber: data.idNumber,
      givenName: data.givenName,
      middleName: data.middleName ?? '',
      familyName: data.familyName,
      maternalFamilyName: data.maternalFamilyName ?? '',
      administrativeSex: data.administrativeSex,
      emailAddress: data.emailAddress,
      birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
      city: data.city,
      state: data.state,
      birthCountry: data.country ?? '',
      region: data.regional?.id ?? data.region,
      specimenType: data.specimenType?.id,
      admissionDate: data.admissionDate ? data.admissionDate.split('T')[0] : '',
      examName: data.examGroup?.name ?? '',
      examCode: data.examCode,
      examType:
        data.examNameId ??
        (data.examGroup?.id ? String(data.examGroup.id) : ''),
      placerOrderNumber: data.placerOrderNumber,
      site: data.site?.id,
      priorityId: data.priority?.id ?? ''
    });

    const examId =
      data.examNameId ??
      (data.examGroup?.id ? String(data.examGroup.id) : null);
    if (examId) {
      this.checkExamTypeForFamilyMembers(examId);
    }
  }

  override cancelSample(): void {
    if (
      confirm('¿Está seguro de cancelar? Los datos no guardados se perderán.')
    ) {
      this.clearSearch();
    }
  }

  saveSample(): void {
    if (this.sampleForm.invalid) {
      this.showValidationErrors();
      return;
    }

    if (!this.caseNumber()) {
      this.toastService.setToast({
        type: ToastTypeEnum.ERROR,
        title: this.MSG_ERROR_GUARDAR,
        message:
          'No se encontró el número de caso. Por favor, realice la búsqueda nuevamente.'
      });
      return;
    }

    const payload = this.buildSaveSamplePayload();

    this.presenter.saveSample(this.caseNumber(), payload).subscribe({
      next: (response: EntryByCodeResponse) =>
        this.onSaveSampleSuccessWithResponse(response),
      error: (error: HttpErrorResponse) => this.onSaveSampleError(error)
    });
  }

  private showValidationErrors(): void {
    this.sampleForm.markAllAsTouched();

    const fieldLabels: Record<string, string> = {
      identifierType: 'Tipo de identificación',
      idNumber: 'Número de documento',
      givenName: 'Nombres',
      familyName: 'Apellidos',
      administrativeSex: 'Sexo',
      birthDate: 'Fecha de nacimiento',
      city: 'Ciudad',
      state: 'Departamento',
      birthCountry: 'País',
      region: 'Ciudad de ingreso',
      specimenType: 'Tipo de muestra',
      examType: 'Examen solicitado',
      examCode: 'Código del examen',
      placerOrderNumber: 'Código orden',
      site: 'Sede toma de muestra',
      admissionDate: 'Fecha de ingreso',
      laboratoriesId: 'Laboratorio de referencia',
      diagnosesId: 'Diagnóstico',
      priorityId: 'Prioridad',
      patientNumber: 'Número de paciente',
      maritalStatus: 'Estado civil',
      race: 'Raza/Etnia',
      telephoneNumber: 'Teléfono',
      streetAddress: 'Dirección',
      specimenContainerId: 'Contenedor de muestra',
      companyId: 'Compañía aseguradora',
      providerId: 'Proveedor',
      causeReprocessId: 'Causa de reproceso',
      statusReprocessId: 'Estado de reproceso',
      reprocessDescription: 'Observación del reproceso'
    };

    const invalidFields = this.getInvalidFieldLabels(fieldLabels);

    const message =
      invalidFields.length > 0
        ? `Campos obligatorios faltantes: ${invalidFields.join(', ')}`
        : 'Por favor complete todos los campos obligatorios antes de guardar.';

    this.toastService.setToast({
      type: ToastTypeEnum.ERROR,
      title: 'Formulario incompleto',
      message
    });
  }

  private getInvalidFieldLabels(fieldLabels: Record<string, string>): string[] {
    const invalidFields: string[] = [];
    Object.keys(this.sampleForm.controls).forEach((key) => {
      const control = this.sampleForm.get(key);
      if (control?.invalid && control?.errors?.['required']) {
        invalidFields.push(fieldLabels[key] || key);
      }
    });
    return invalidFields;
  }

  private buildSaveSamplePayload(): SaveSampleByCodePayload {
    const formValue = this.sampleForm.getRawValue() as SampleFormValue;

    return {
      datosOrden: this.buildDatosOrden(formValue),
      datosReproceso: this.buildDatosReproceso(formValue)
    };
  }

  private buildDatosOrden(
    formValue: SampleFormValue
  ): SaveSampleByCodePayload['datosOrden'] {
    return {
      laboratoriesId: formValue.laboratoriesId ?? '',
      diagnosesId: formValue.diagnosesId ?? '',
      specialistsId: formValue.specialistsId ?? '',
      siteId: formValue.site,
      regionalId: formValue.region,
      consentForUseOfSamples: formValue.consentForUseOfSamples,
      userDataDescription: formValue.userDataDescription ?? '',
      descriptionDataSample: formValue.descriptionDataSample ?? '',
      descriptionDataExam: formValue.descriptionDataExam ?? '',
      clinicalHistory: formValue.clinicalHistory ?? '',
      prioritization: formValue.prioritization,
      acmg: formValue.acmg,
      mlpa: formValue.mlpa,
      priorityId: formValue.priorityId ?? '',
      providerId: formValue.providerId ?? '',
      companyId: formValue.companyId ?? ''
    };
  }

  private buildDatosReproceso(
    formValue: SampleFormValue
  ): SaveSampleByCodePayload['datosReproceso'] {
    const isReprocess = formValue.isReprocess;

    if (!isReprocess) {
      return {
        isReprocess: false,
        causeReprocessId: null,
        statusReprocessId: null,
        reprocessDescription: null
      };
    }

    return {
      isReprocess: true,
      causeReprocessId: formValue.causeReprocessId || null,
      statusReprocessId: formValue.statusReprocessId || null,
      reprocessDescription: formValue.reprocessDescription || null
    };
  }

  private onSaveSampleSuccess(): void {
    this.toastService.setToast({
      title: 'Muestra guardada exitosamente',
      type: ToastTypeEnum.SUCCESS
    });
    setTimeout(() => {
      this.toastService.setToast(null);
    }, 10000);
    this.clearSearch();
  }

  private onSaveSampleSuccessWithResponse(response: EntryByCodeResponse): void {
    if (
      response.requiresFamilyMembers &&
      response.lgNumber &&
      response.examType
    ) {
      this.savedLgNumber.set(response.lgNumber);
      this.savedOrderId.set(response.orderId || '');
      this.savedExamType.set(response.examType);
      this.savedExamTypeId.set(
        response.examTypeId || this.sampleForm.get('examType')?.value || ''
      );
      this.savedPlacerOrderNumber.set(
        response.placerOrderNumber ||
          this.sampleForm.get('placerOrderNumber')?.value ||
          ''
      );
      this.showForm.set(false);
      this.toastService.setToast({
        title: 'Muestra del afecto guardada',
        message: `LG: ${response.lgNumber}. Ahora debe ingresar los datos de los familiares.`,
        type: ToastTypeEnum.SUCCESS
      });

      this.showFamilyMembersModal.set(true);
      if (this.familyMembersModal) {
        this.familyMembersModal.open();
      }

      setTimeout(() => {
        this.toastService.setToast(null);
      }, 5000);
    } else {
      this.onSaveSampleSuccess();
    }
  }

  private onSaveSampleError(error: any): void {
    const backendMessage = this.extractBackendMessage(error);

    const fallbackMessages: Record<number, string> = {
      400: 'Datos inválidos en el formulario',
      401: 'Sesión expirada, por favor inicia sesión nuevamente',
      403: 'No tienes permisos para guardar muestras'
    };

    const errorMessage =
      backendMessage || fallbackMessages[error.status] || 'Error al guardar la muestra';

    this.toastService.setToast({
      title: this.MSG_ERROR_GUARDAR,
      message: errorMessage,
      type: ToastTypeEnum.ERROR
    });
    setTimeout(() => {
      this.toastService.setToast(null);
    }, 10000);
  }

  saveManualSample(): void {
    const formValue = this.sampleForm.getRawValue() as SampleFormValue;

    if (!this.validateRequiredFields(formValue)) {
      return;
    }

    const specimenId = formValue.specimenId || `SPEC-${Date.now()}`;
    const birthDate = this.formatBirthDate(formValue.birthDate);
    const basePayload = this.buildManualPayload(formValue, specimenId, birthDate);

    const payload: ManualEntryPayload = formValue.isReprocess
      ? {
          ...basePayload,
          datosReproceso: {
            reprocessStatusId: formValue.statusReprocessId || undefined,
            reprocessCauseId: formValue.causeReprocessId || undefined,
            reprocessObservation: formValue.reprocessDescription || undefined
          }
        }
      : basePayload;

    this.presenter.saveManualSample(payload).subscribe({
      next: (response: ManualEntryResponse) =>
        this.handleManualSaveSuccess(response),
      error: (error: HttpErrorResponse) => this.handleManualSaveError(error)
    });
  }

  private validateRequiredFields(formValue: SampleFormValue): boolean {
    const requiredFields = [
      { field: 'idNumber', label: 'Número de documento' },
      { field: 'identifierType', label: 'Tipo de identificación' },
      { field: 'givenName', label: 'Nombres' },
      { field: 'familyName', label: 'Apellidos' },
      { field: 'administrativeSex', label: 'Sexo' },
      { field: 'birthDate', label: 'Fecha de nacimiento' },
      { field: 'maritalStatus', label: 'Estado civil' },
      { field: 'telephoneNumber', label: 'Teléfono' },
      { field: 'region', label: 'Ciudad de ingreso' },
      { field: 'specimenType', label: 'Tipo de muestra' },
      { field: 'specimenContainerId', label: 'ID contenedor de muestra' },
      { field: 'examType', label: 'Examen solicitado' },
      { field: 'placerOrderNumber', label: 'Código orden' },
      { field: 'caseNumberInput', label: 'Número de caso' },
      { field: 'site', label: 'Sede toma de muestra' },
      { field: 'priorityId', label: 'Prioridad' },
      { field: 'laboratoriesId', label: 'Laboratorio de referencia' },
      { field: 'diagnosesId', label: 'Diagnóstico' },
      { field: 'companyId', label: 'ID de compañía' },
      { field: 'providerId', label: 'ID de proveedor' }
    ];

    const missingFields = requiredFields.filter(
      (f) => !formValue[f.field as keyof SampleFormValue]
    );

    if (missingFields.length > 0) {
      this.toastService.setToast({
        title: 'Formulario incompleto',
        message: `Faltan campos obligatorios: ${missingFields.map((f) => f.label).join(', ')}`,
        type: ToastTypeEnum.WARNING
      });
      return false;
    }
    return true;
  }

  private formatBirthDate(birthDateValue: string | Date | undefined): string {
    if (!birthDateValue) {
      return '';
    }

    if (typeof birthDateValue === 'string') {
      return birthDateValue.includes('T')
        ? birthDateValue.split('T')[0]
        : birthDateValue;
    }

    const dateObj = new Date(birthDateValue);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildManualPayload(
    formValue: SampleFormValue,
    specimenId: string,
    birthDate: string
  ): ManualEntryPayload {
    return {
      datosPaciente: {
        idNumber: formValue.idNumber,
        identifierTypeCode: formValue.identifierType,
        patientNumber: formValue.patientNumber,
        givenName: formValue.givenName,
        middleName: formValue.middleName,
        familyName: formValue.familyName,
        maternalFamilyName: formValue.maternalFamilyName,
        emailAddress: formValue.emailAddress,
        birthDate,
        administrativeSex: formValue.administrativeSex,
        maritalStatus: formValue.maritalStatus,
        race: formValue.race,
        telephoneNumber: formValue.telephoneNumber,
        streetAddress: formValue.streetAddress,
        city: formValue.city,
        state: formValue.state,
        country: formValue.birthCountry
      },
      datosMuestra: {
        specimenId,
        specimenTypeId: formValue.specimenType,
        specimenContainerId: formValue.specimenContainerId,
        priorityId: formValue.priorityId,
        reprocess: formValue.isReprocess
      },
      datosExamen: {
        examId: formValue.examType
      },
      datosOrden: {
        companyId: formValue.companyId,
        providerId: formValue.providerId,
        regionalId: formValue.region,
        siteId: formValue.site,
        placerOrderNumber: formValue.placerOrderNumber,
        caseNumber: formValue.caseNumberInput,
        consentForUseOfSamples: formValue.consentForUseOfSamples,
        userDataDescription: formValue.userDataDescription,
        descriptionDataSample: formValue.descriptionDataSample,
        descriptionDataExam: formValue.descriptionDataExam,
        clinicalHistory: formValue.clinicalHistory,
        prioritization: formValue.prioritization,
        acmg: formValue.acmg,
        mlpa: formValue.mlpa,
        laboratoryId: formValue.laboratoriesId,
        diagnosisId: formValue.diagnosesId,
        specialistId: formValue.specialistsId
      }
    };
  }

  private handleManualSaveSuccess(response: ManualEntryResponse): void {
    if (
      response.requiresFamilyMembers &&
      response.lgNumber &&
      response.examType
    ) {
      this.savedLgNumber.set(response.lgNumber);
      this.savedOrderId.set(response.orderId || '');
      this.savedExamType.set(response.examType);
      this.savedExamTypeId.set(
        response.examTypeId || this.sampleForm.get('examType')?.value || ''
      );
      this.savedPlacerOrderNumber.set(
        response.placerOrderNumber ||
          this.sampleForm.get('placerOrderNumber')?.value ||
          ''
      );

      this.showForm.set(false);

      this.toastService.setToast({
        title: 'Muestra del afecto guardada',
        message: `LG: ${response.lgNumber}. Ahora debe ingresar los datos de los familiares.`,
        type: ToastTypeEnum.SUCCESS
      });

      this.showFamilyMembersModal.set(true);
      if (this.familyMembersModal) {
        this.familyMembersModal.open();
      }

      setTimeout(() => {
        this.toastService.setToast(null);
      }, 5000);
    } else {
      this.toastService.setToast({
        title: 'Ingreso manual exitoso',
        message: 'La muestra se ha registrado correctamente',
        type: ToastTypeEnum.SUCCESS
      });
      setTimeout(() => {
        this.toastService.setToast(null);
      }, 10000);
      this.clearSearch();
    }
  }

  onFamilyMembersSaved(response: CreateFamilyMembersResponse): void {
    this.showFamilyMembersModal.set(false);
    this.toastService.setToast({
      title: 'Familiares registrados exitosamente',
      message: `Padre: ${response.lgPadre || 'N/A'} | Madre: ${response.lgMadre || 'N/A'}`,
      type: ToastTypeEnum.SUCCESS
    });
    setTimeout(() => {
      this.toastService.setToast(null);
    }, 10000);
    this.clearSearch();
    if (this.isModal) {
      setTimeout(() => {
        this.sampleCompleted.emit();
      }, 1500);
    }
  }

  onFamilyModalClosed(): void {
    this.showFamilyMembersModal.set(false);
    this.toastService.setToast({
      title: 'Familiares pendientes',
      message:
        'Debe ingresar los familiares para completar el proceso. Puede hacerlo desde el listado de órdenes.',
      type: ToastTypeEnum.WARNING
    });
    setTimeout(() => {
      this.toastService.setToast(null);
    }, 10000);
    this.clearSearch();
  }

  private handleManualSaveError(error: any): void {
    const errorMessage = this.getManualSaveErrorMessage(error);
    this.toastService.setToast({
      title: this.MSG_ERROR_GUARDAR,
      message: errorMessage,
      type: ToastTypeEnum.ERROR
    });
    setTimeout(() => {
      this.toastService.setToast(null);
    }, 10000);
  }

  private getManualSaveErrorMessage(error: any): string {
    const backendMessage = this.extractBackendMessage(error);
    if (backendMessage) {
      return backendMessage;
    }

    const fallbackMessages: Record<number, string> = {
      400: 'Datos inválidos en el formulario. Verifique los campos',
      401: 'Sesión expirada, por favor inicia sesión nuevamente',
      403: 'No tienes permisos para realizar ingresos manuales'
    };

    return fallbackMessages[error.status] || 'Error al guardar el ingreso manual';
  }

  openFamilyMemberModal(type?: string): void {
    if (type) {
      this.selectedKinshipType =
        type === 'PADRE' ? KinshipType.PADRE : KinshipType.MADRE;
    }
    if (this.manualEntryModal && this.selectedKinshipType) {
      this.manualEntryModal.openAsModal(this.selectedKinshipType);
    }
  }

  onFamilyMemberAdded(familyMember: FamilyMemberData): void {
    this.addFamilyMember(familyMember);
  }

  identifierTypesForModal(): { id: string; name: string }[] {
    return this.identifierTypeDropdownOptions.map((opt) => ({
      id: opt.value,
      name: opt.label
    }));
  }

  getFamilyMember(kinshipType: KinshipType): FamilyMemberData | undefined {
    return this.familyMembers().find((m) => m.tipoParentesco === kinshipType);
  }

  override ngOnDestroy(): void {
    this.toastService.setToast(null);
  }
}
